import { AgentType, APIAttachment, ApiDocumentReference, ArdoqId, HistoricChat, LoadedChat, } from '@ardoq/api-types';
import { CSRF_HEADER_NAME, Method, deleteEntity, fetchEntities, fetchJson, fetchVoid, getCsrfToken, toRequestOptions, updateEntity, } from './api';
import { ApiResponse } from './types';
const getUserUrl = (): string => '/api/attachment/user';
const getItemFromUserUrl = (userId: ArdoqId, itemId: ArdoqId): string => `${getUserUrl()}/${userId}/${itemId}`;
export type AiControlledCommandManifestEntry = {
    /** The command id corresponds to the key by which it is keyed in the AiControlledCommandDomain object */
    id: string;
    /** Natural-language description of what the command does and when to use it. */
    description: string;
    responsePayloadSchema: object;
    /** Natural-language description of the delivered `context`. */
    contextDescription: string;
    context: object | null;
};
export type AiControlledCommandDomain = {
    uniqueDomainName: string;
    commands: Record<string, AiControlledCommandManifestEntry>;
};
/** One applicable AI-controlled command as advertised to the assistant. */
export type UiCommandManifestEntry = {
    /** Stable wire id the assistant calls the command by. */
    name: string;
    /** The asset area the command operates in (e.g. 'viewpoint'). */
    domain: string;
    /** What it does + when to use it — the assistant's selection signal. */
    description: string;
    /** Full JSON Schema envelope for the payload (`typia.json.schema` output). */
    parameters: object;
    /** The command's live on-screen state, revealed lazily to the sub-agent. */
    context: object | null;
    /** How to interpret `context`. */
    contextDescription: string;
};
/** One entry of the per-view domain menu — an asset area plus its description.
 * Lets the main agent classify a request without touching the cached prefix. */
export type DomainMenuEntry = {
    domain: string;
    description: string;
};
/** The versioned `uiCommands` request payload — the successor to `availableFrontendTools`.
 * Carries the applicable commands and the domain menu. The asset identity the commands act on
 * comes from the per-message `assetContext`, not from here. */
export type UiCommandsRequestPayload = {
    version: 1;
    commands: UiCommandManifestEntry[];
    domains: DomainMenuEntry[];
};
export type SendMessageRequest = {
    content: string;
    reportId?: string | null;
    chatSessionId?: string | null;
    clientRequestId: string;
    agentType?: AgentType;
    context?: object & {
        assetType: string;
        _id?: string;
    };
    availableFrontendTools?: AiControlledCommandDomain[];
    uiCommands?: UiCommandsRequestPayload;
    documents?: ApiDocumentReference[];
    webSearchEnabled?: boolean;
};
const sendMessageWithHistory = ({ content, clientRequestId, chatSessionId, agentType, context, availableFrontendTools, uiCommands, documents, webSearchEnabled, }: SendMessageRequest): ApiResponse<{
    session: HistoricChat;
}> => {
    if (context?.assetType === 'report' && agentType === 'report_insight') {
        const body = {
            newMessage: content,
            chatSessionId,
            assetContext: context,
            reportId: context._id,
            agentType: agentType,
            documents,
        };
        const request = new Request(`/api/chat/complete-with-history?requestId=${clientRequestId}`, toRequestOptions(Method.POST, body));
        return fetchJson(request);
    }
    const body = {
        newMessage: content,
        chatSessionId,
        assetContext: context,
        availableFrontendTools,
        uiCommands,
        // For Front end we only set the agent type when actual agent is in use.
        // the ardoq_assistant is not an agent but we need this fallback value for backend api.
        agentType: agentType ?? 'ardoq_assistant',
        documents,
        webSearchEnabled,
    };
    const request = new Request(`/api/chat/complete/v2?requestId=${clientRequestId}`, toRequestOptions(Method.POST, body));
    return fetchJson(request);
};
const sendCancellation = ({ clientRequestId, }: {
    clientRequestId: string;
}): ApiResponse<void> => {
    const request = new Request(`/api/chat/cancel?requestId=${clientRequestId}`, toRequestOptions(Method.POST, {}));
    return fetchVoid(request);
};
const pinHistoricChat = async (chat: HistoricChat): ApiResponse<HistoricChat> => {
    return updateEntity<HistoricChat>('chat/session')({
        ...chat,
        pinned: true,
    });
};
const unpinHistoricChat = async (chat: HistoricChat): ApiResponse<HistoricChat> => {
    return updateEntity<HistoricChat>('chat/session')({
        ...chat,
        pinned: false,
    });
};
const fetchHistoricChatContent = (id: string): ApiResponse<LoadedChat> => {
    const request = new Request(`/api/chat/session/${id}/messages`);
    return fetchJson<LoadedChat>(request);
};
export type UploadUserDocumentPayload = {
    file: File;
    chatSessionId?: string | null;
};
const uploadUserDocument = ({ file, chatSessionId, }: UploadUserDocumentPayload): ApiResponse<APIAttachment> => {
    const url = chatSessionId
        ? `${getUserUrl()}/upload?chatSessionId=${chatSessionId}`
        : `${getUserUrl()}/upload`;
    const token = getCsrfToken();
    const csrfHeader = token ? { [CSRF_HEADER_NAME]: token } : undefined;
    const formData = new FormData();
    formData.append('attachment', file);
    return fetchJson<APIAttachment>(new Request(url, {
        method: Method.POST,
        headers: csrfHeader,
        body: formData,
    }));
};
const deleteUserDocument = ({ userId, documentId, }: {
    userId: ArdoqId;
    documentId: ArdoqId;
}): ApiResponse<void> => {
    const token = getCsrfToken();
    const csrfHeader = token ? { [CSRF_HEADER_NAME]: token } : undefined;
    return fetchVoid(new Request(getItemFromUserUrl(userId, documentId), {
        method: Method.DELETE,
        headers: csrfHeader,
    }));
};
export const assistantApi = {
    sendMessageWithHistory,
    sendCancellation,
    fetchHistoricChats: fetchEntities<HistoricChat>('chat/session'),
    fetchHistoricChatContent,
    deleteHistoricChat: deleteEntity('chat/session'),
    pinHistoricChat,
    unpinHistoricChat,
    uploadUserDocument,
    deleteUserDocument,
    getUserUrl,
    getItemFromUserUrl,
};
